import type { LegalCommon, LegalDocument } from "@/components/LegalPageLayout";

const legalEyebrow = "Legal";
const updated = "Last updated: 1 August 2026";

/**
 * Translation of legal-sl.ts. Slovenian is the authoritative version (see the
 * closing clause of the Terms); keep this file in step whenever that one moves.
 */
export const legalEn = {
  common: {
    tocHeading: "Contents",
    company: {
      legalName: "Company",
      address: "Registered office",
      registrationNumber: "Registration no.",
      vatNumber: "VAT ID",
      taxNumber: "Tax number",
      registryEntry: "Register entry",
      shareCapital: "Share capital",
      representative: "Represented by",
      email: "Email",
      phone: "Phone",
      website: "Website",
    },
  } satisfies LegalCommon,

  terms: {
    eyebrow: legalEyebrow,
    title: "Terms of Business and Use",
    updated,
    intro:
      "These terms govern the use of the nreport.ai website and the ordering of property reports. The website and the service are operated by Noventis d.o.o. Please read them carefully — by using the service you confirm that you have read and accepted them.",
    sections: [
      {
        title: "1. Provider information",
        blocks: [
          {
            type: "p",
            text: "The nreport.ai website and the nReport service are operated and provided by:",
          },
          { type: "company" },
          {
            type: "p",
            text: "This information is published pursuant to Article 6 of the Slovenian Electronic Commerce Market Act (ZEPT) and Article 45 of the Companies Act (ZGD-1). Below, Noventis d.o.o. is referred to as the “provider”, “we” or “nReport”, and the person using the service as the “user” or “you”.",
          },
        ],
      },
      {
        title: "2. Scope and acceptance",
        blocks: [
          {
            type: "p",
            text: "These terms apply to all visitors of the website, registered users and buyers of reports, both consumers and business customers. They form an integral part of every contract concluded through the website.",
          },
          {
            type: "p",
            text: "You accept the terms when you place an order. Before completing a purchase, the terms are always available to you in a form you can save and print. If you do not accept them, you must not use the service.",
          },
          {
            type: "p",
            text: "A consumer is a natural person acquiring or using the service for purposes outside their trade or profession. Provisions expressly marked as consumer provisions apply to consumers only; the mandatory provisions of the Consumer Protection Act (ZVPot-1) apply regardless of these terms and are not limited by them.",
          },
        ],
      },
      {
        title: "3. Description of the service",
        blocks: [
          {
            type: "p",
            text: "nReport is an online service that automatically collects data on an individual property in the Republic of Slovenia from publicly accessible official registers and publicly published listings, processes it, and produces a structured report as a PDF document.",
          },
          { type: "p", text: "A report typically contains:" },
          {
            type: "ul",
            items: [
              "data from the real estate cadastre and the real estate register (GURS),",
              "data on completed transactions from the Real Estate Market Register (ETN),",
              "information on the land registry status where an excerpt is available,",
              "energy performance certificate data where available,",
              "an overview of comparable publicly published listings,",
              "a description of the location and its surroundings,",
              "an indicative market value estimate and a written analysis produced with the help of artificial intelligence.",
            ],
          },
          {
            type: "p",
            text: "The exact content depends on data availability for the specific property. If a source is unavailable or holds no record for the property, that part of the report may be reduced or omitted; this does not constitute defective performance.",
          },
        ],
      },
      {
        title: "4. Nature of the report — what it is not",
        blocks: [
          {
            type: "note",
            text: "An nReport report is an informational aid. It is not an official appraisal and not a valuation prepared under the Auditing Act (ZRev-2) and the International Valuation Standards. It is not produced by a certified real estate appraiser and cannot replace such an appraisal.",
          },
          { type: "p", text: "The report is also not:" },
          {
            type: "ul",
            items: [
              "legal, tax, accounting or investment advice,",
              "a substitute for an official land registry excerpt or a certified cadastral excerpt,",
              "a substitute for an on-site inspection, a structural survey or legal due diligence,",
              "a basis for a decision by a lender, insurer or public authority,",
              "evidence in judicial, tax or administrative proceedings.",
            ],
          },
          {
            type: "p",
            text: "Before entering into a property transaction we recommend obtaining official land registry and cadastral excerpts and consulting a lawyer, notary, real estate agent or certified appraiser.",
          },
        ],
      },
      {
        title: "5. Use of artificial intelligence",
        blocks: [
          {
            type: "p",
            text: "The written part of the report and the value estimate are generated automatically using artificial intelligence systems (large language models) and statistical models. As a rule, the content is not reviewed by a human before delivery.",
          },
          {
            type: "p",
            text: "Despite careful design, AI systems can produce inaccurate, incomplete or misleading statements, even when the input data is correct. Verify any statement in the report you intend to rely on against the original official source.",
          },
          {
            type: "p",
            text: "We do not use the data you enter into the service, or the content of the reports produced, to train AI models, and we contractually prohibit the model providers we use from doing so.",
          },
        ],
      },
      {
        title: "6. Data sources and their currency",
        blocks: [
          {
            type: "p",
            text: "Reports are based on publicly accessible sources, including the registers of the Surveying and Mapping Authority of the Republic of Slovenia (real estate cadastre, real estate register, Real Estate Market Register), the land registry, the register of energy performance certificates, mapping sources, and publicly published property listings.",
          },
          {
            type: "p",
            text: "The provider is not the originator of this data and has no influence over its accuracy, completeness or currency. Official registers are updated with a delay and their content may deviate from the actual state of the property (for example due to unreported works, unsynchronised entries or errors in the source register). Data taken from listings reflects the price asked by the advertiser, not the price achieved.",
          },
          {
            type: "p",
            text: "All data in a report relates to the moment the report was produced. Reports are not updated automatically afterwards.",
          },
        ],
      },
      {
        title: "7. User account",
        blocks: [
          {
            type: "ul",
            items: [
              "Accessing ordered reports requires a user account with a valid email address. You can create an account with a password or by signing in with Google or Facebook.",
              "If you purchase without registering first, we create an account automatically for the email address you provide at payment and send you a sign-in link.",
              "The information you provide must be truthful and up to date.",
              "You are responsible for safeguarding your password and for all use of the service through your account. Notify us immediately of any suspected unauthorised use.",
              "An account is intended for one person or one business and may not be transferred or shared.",
              "The service is not intended for persons under 18 years of age.",
              "You may delete your account at any time; see the data deletion section of the Privacy Policy.",
            ],
          },
        ],
      },
      {
        title: "8. Credits, prices and payment",
        blocks: [
          {
            type: "ul",
            items: [
              "Reports are paid for with credits. One credit produces and unlocks one full report for one property.",
              "Prices are published on the website and stated in euros (EUR). The price shown is the final price you pay. The provider is not registered for VAT, so no VAT is charged (Article 94(1) of the Slovenian VAT Act, ZDDV-1).",
              "There are no delivery costs, as reports are delivered electronically.",
              "Payment is made in advance through the payment service provider Stripe. The provider neither receives nor stores your payment card details.",
              "Invoices are issued electronically and sent to your email address. By placing an order you consent to receiving invoices in electronic form.",
              "Purchased credits are valid for 1 year from the date of purchase, as stated in the pricing table. Unused credits are not replaced after they expire unless otherwise agreed.",
              "Credits are not redeemable for cash, except where required by law or by these terms (for example on withdrawal from the contract).",
            ],
          },
        ],
      },
      {
        title: "9. Conclusion of the contract and performance",
        blocks: [
          {
            type: "p",
            text: "The presentation of the service on the website is an invitation to make an offer. The contract is concluded when you place an order by clicking a button that expressly states the obligation to pay and the payment is successfully confirmed. We send you an order confirmation by email together with these terms on a durable medium.",
          },
          {
            type: "p",
            text: "Production of the report begins immediately after payment is confirmed, or immediately after you spend a credit on a chosen property. A report is usually available within minutes; where data must be retrieved from slow or temporarily unavailable external registers it may take longer, but no later than 48 hours. The report is available in your user account and a copy is also sent by email.",
          },
          {
            type: "p",
            text: "If a report cannot be produced for reasons on our side, we return the credit to your account or, at your request, refund the price paid.",
          },
        ],
      },
      {
        title: "10. Consumer right of withdrawal",
        blocks: [
          {
            type: "p",
            text: "Under Article 134 of ZVPot-1, a consumer has the right to withdraw from a distance contract within 14 days of its conclusion without giving a reason. An unequivocal statement sent to info@nreport.ai is sufficient; you may also use the official Slovenian withdrawal form.",
          },
          {
            type: "p",
            text: "For credit purchases this means: unused credits are refunded in full within 14 days without explanation. Refunds are made using the same means of payment, no later than 14 days after we receive the withdrawal statement.",
          },
          {
            type: "note",
            text: "Exception for a report already produced: under Article 135(1)(13) of ZVPot-1, a consumer has no right of withdrawal for digital content not supplied on a tangible medium if performance began with the consumer's express prior consent and with their acknowledgement that they thereby lose the right of withdrawal. We therefore ask for that consent explicitly before payment. Once the report for the chosen property has been produced and made available to you, there is no right of withdrawal for the credit spent.",
          },
          {
            type: "p",
            text: "If you do not give that consent, a purchase cannot be completed through the website; in that case contact us at info@nreport.ai and we will arrange an alternative.",
          },
          {
            type: "p",
            text: "The right of withdrawal does not apply to legal entities, sole traders or other persons ordering the service in the course of their business.",
          },
        ],
      },
      {
        title: "11. Conformity of digital content and remedies",
        blocks: [
          {
            type: "p",
            text: "We undertake to supply a report that conforms to the contract within the meaning of the ZVPot-1 provisions on digital content and digital services: it matches the description in these terms and on the website, is fit for the ordinary purpose of such content, and is supplied in the agreed scope.",
          },
          {
            type: "p",
            text: "Where a report does not conform, a consumer may require conformity to be brought about (re-production or completion of the report), a price reduction, or withdrawal from the contract under the conditions laid down in ZVPot-1. Report non-conformity to info@nreport.ai with a description of the defect. We respond within 8 days at the latest.",
          },
          {
            type: "p",
            text: "A discrepancy between official register data and the actual state of the property is not a non-conformity of the report, since the provider does not create that data and does not warrant it — see sections 6 and 12.",
          },
        ],
      },
      {
        title: "12. Limitation of liability",
        blocks: [
          {
            type: "p",
            text: "The service is designed as an informational aid. The provider does not warrant the accuracy, completeness or currency of data taken from external sources, nor the accuracy of value estimates or written analyses generated with the help of artificial intelligence.",
          },
          {
            type: "p",
            text: "By using the service you agree not to base a decision to buy, sell, let, finance or value a property solely on a report. The provider is not liable for business decisions taken on the basis of a report, nor for lost profit, lost opportunity or other indirect or consequential damage.",
          },
          {
            type: "p",
            text: "The provider's aggregate liability arising from an individual order is limited to the amount the user paid for that order, unless mandatory law provides otherwise.",
          },
          {
            type: "note",
            text: "Nothing in these terms excludes or limits the provider's liability for intent or gross negligence, for damage to life or health, or the statutory rights of consumers under ZVPot-1 and the Obligations Code.",
          },
          {
            type: "p",
            text: "The service is provided “as is”. We aim for uninterrupted operation but do not warrant that the service will always be available without errors or interruptions; scheduled maintenance and outages of external data sources are to be expected.",
          },
        ],
      },
      {
        title: "13. Permitted use and prohibitions",
        blocks: [
          { type: "p", text: "When using the service, you must not:" },
          {
            type: "ul",
            items: [
              "systematically, automatically or in bulk extract website content (for example by scraping, bots or data mining),",
              "attempt to access parts of the system you are not entitled to, or circumvent security measures,",
              "overload the infrastructure or disrupt the operation of the service,",
              "use the service to harass, stalk or profile individuals,",
              "order reports for properties where you have no legitimate interest,",
              "resell, distribute, publish or incorporate reports or parts of them into your own products without our prior written permission,",
              "remove or obscure attributions, source references and disclaimers contained in a report.",
            ],
          },
          {
            type: "p",
            text: "In the event of a breach we may restrict or suspend access, and in the event of serious or repeated breaches we may close the account. We will notify you of the measure and, where possible, give you an opportunity to remedy the breach.",
          },
        ],
      },
      {
        title: "14. Intellectual property and licence to use a report",
        blocks: [
          {
            type: "p",
            text: "The website, its design, source code, texts, graphics, the nReport trade mark, and the structure, selection and presentation of data in reports are protected by copyright and by the sui generis database right (Copyright and Related Rights Act). All rights reserved.",
          },
          {
            type: "p",
            text: "By purchasing a report you acquire a non-exclusive, non-transferable and perpetual right to use the report for your own purposes, including sharing it with your advisers, bank, real estate agent or counterparty in a specific transaction. Any wider use, in particular publication, resale or systematic reproduction, requires our prior written permission.",
          },
          {
            type: "p",
            text: "Data from official public registers belongs to or is administered by the competent authorities and is used in a report as information about the publicly accessible position; the rights of those authorities are not transferred to the user.",
          },
        ],
      },
      {
        title: "15. Sharing a report by link",
        blocks: [
          {
            type: "p",
            text: "The service lets you create a sharing link for your report. Anyone who knows the link can view the report without signing in, so share it carefully. You can revoke the link at any time in your account; once revoked, the report is no longer accessible through it.",
          },
          {
            type: "p",
            text: "You are responsible for the content you share and for the consequences of sharing it.",
          },
        ],
      },
      {
        title: "16. Protection of personal data",
        blocks: [
          {
            type: "p",
            text: "We process personal data in accordance with the General Data Protection Regulation (GDPR) and the Slovenian Personal Data Protection Act (ZVOP-2). Details of the categories of data, purposes, legal bases, retention periods, processors and your rights are set out in the Privacy Policy, which forms an integral part of these terms.",
          },
        ],
      },
      {
        title: "17. Complaints and out-of-court dispute resolution",
        blocks: [
          {
            type: "p",
            text: "Complaints may be sent to info@nreport.ai. We acknowledge receipt within 5 working days and inform you of the outcome within 8 days at the latest. The complaints procedure is confidential.",
          },
          {
            type: "p",
            text: "Pursuant to Article 32 of the Out-of-Court Consumer Dispute Resolution Act (ZIsRPS), we state that the provider does not recognise any out-of-court consumer dispute resolution body as competent to resolve consumer disputes. This does not affect a consumer's right to bring proceedings before a court or to contact the competent authorities.",
          },
          {
            type: "p",
            text: "The European Online Dispute Resolution platform ceased operating on 20 July 2025 under Regulation (EU) 2024/3228, so we no longer publish a link to it.",
          },
          {
            type: "p",
            text: "Consumers may also contact the Market Inspectorate of the Republic of Slovenia, Dunajska cesta 160, 1000 Ljubljana, or the European Consumer Centre Slovenia for cross-border disputes within the EU.",
          },
        ],
      },
      {
        title: "18. Changes to the terms and the service",
        blocks: [
          {
            type: "p",
            text: "We may amend these terms because of changes in legislation, changes to the service, or to remove ambiguity. The version in force is always published on this page with the date of the last update.",
          },
          {
            type: "p",
            text: "We notify registered users of material changes by email at least 15 days before they take effect. If you do not accept a change, you may stop using the service before it takes effect and request deletion of your account; in that case we refund the price paid for unused credits.",
          },
          {
            type: "p",
            text: "Orders already placed are governed by the terms in force when the order was placed.",
          },
        ],
      },
      {
        title: "19. Governing law and jurisdiction",
        blocks: [
          {
            type: "p",
            text: "Contractual relationships under these terms are governed by the law of the Republic of Slovenia, excluding its conflict-of-law rules. The UN Convention on Contracts for the International Sale of Goods does not apply.",
          },
          {
            type: "p",
            text: "Disputes fall within the jurisdiction of the competent court in the Republic of Slovenia. For consumers, this choice does not deprive them of the protection afforded by the mandatory rules of the country of their habitual residence; a consumer may also bring proceedings before the courts of their country of residence.",
          },
        ],
      },
      {
        title: "20. Final provisions",
        blocks: [
          {
            type: "p",
            text: "If any provision of these terms is invalid or unenforceable, the validity of the remaining provisions is unaffected; the invalid provision is replaced by a valid one that comes closest to its economic purpose.",
          },
          {
            type: "p",
            text: "These terms are published in Slovenian, English, German and Russian. In the event of any discrepancy between language versions, the Slovenian version prevails.",
          },
          {
            type: "p",
            text: "For questions about these terms, contact us at info@nreport.ai.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  privacy: {
    eyebrow: legalEyebrow,
    title: "Privacy Policy",
    updated,
    intro:
      "This policy explains which personal data Noventis d.o.o. processes when operating the nreport.ai website and providing the nReport service, why it does so, on what legal basis, who it is shared with, how long it is kept, and what rights you have. It is prepared in accordance with Regulation (EU) 2016/679 (GDPR) and the Slovenian Personal Data Protection Act (ZVOP-2).",
    sections: [
      {
        title: "1. Controller and contact details",
        blocks: [
          { type: "p", text: "The controller of personal data is:" },
          { type: "company" },
          {
            type: "p",
            text: "For all data protection questions and to exercise your rights, write to privacy@nreport.ai or to the company's registered address.",
          },
          {
            type: "p",
            text: "Given the nature, scope and purposes of the processing, the controller is not required to appoint a data protection officer (Article 37 GDPR). Should that change, we will publish the contact details here.",
          },
        ],
      },
      {
        title: "2. What data we process",
        blocks: [
          {
            type: "table",
            head: ["Category", "Specific data", "Source"],
            rows: [
              [
                "Account data",
                "email address, name (if provided), hashed password, sign-in provider identifier (Google, Facebook), registration date, language",
                "from you or your sign-in provider",
              ],
              [
                "Order data",
                "address and identification of the property queried, apartment number, property type, reports produced, credit balance, usage history, the time a report was first opened, its PDF downloaded and its negotiating brief viewed",
                "from you and from operation of the service",
              ],
              [
                "Payment data",
                "amount, currency, time, payment status, payment provider session identifier, invoice details. We neither receive nor store payment card data.",
                "from the payment service provider Stripe",
              ],
              [
                "Technical data",
                "IP address, browser and device information, access time, requested URLs, error traces",
                "automatically during use",
              ],
              [
                "Consent records",
                "your cookie choice and its timestamp, newsletter consent, consent to immediate performance at purchase",
                "from you",
              ],
              [
                "Communications",
                "the content of messages you send us and our replies",
                "from you",
              ],
              [
                "Usage and marketing data",
                "in-app events, traffic sources, conversions — only to the extent you have permitted through your cookie consent",
                "automatically, upon consent",
              ],
            ],
          },
          {
            type: "p",
            text: "We do not collect special categories of personal data under Article 9 GDPR (such as health, religious or political data) and ask that you do not send them to us.",
          },
        ],
      },
      {
        title: "3. Purposes and legal bases",
        blocks: [
          {
            type: "table",
            head: ["Purpose", "Legal basis", "Retention"],
            rows: [
              [
                "Managing the user account and sign-in",
                "performance of a contract — Art. 6(1)(b) GDPR",
                "until the account is deleted; then up to 30 days in backups",
              ],
              [
                "Producing and delivering the ordered report",
                "performance of a contract — Art. 6(1)(b) GDPR",
                "for the lifetime of the account, so reports remain accessible",
              ],
              [
                "Processing payments and preventing fraud",
                "performance of a contract — Art. 6(1)(b); legal obligation — Art. 6(1)(c); legitimate interest — Art. 6(1)(f)",
                "per accounting record retention rules",
              ],
              [
                "Issuing and keeping invoices, tax obligations",
                "legal obligation — Art. 6(1)(c) GDPR (ZDDV-1, ZGD-1)",
                "10 years from the end of the year the invoice relates to",
              ],
              [
                "Transactional notifications (order confirmation, report ready, password reset)",
                "performance of a contract — Art. 6(1)(b) GDPR",
                "until the account is deleted",
              ],
              [
                "User support and complaint handling",
                "performance of a contract — Art. 6(1)(b); legitimate interest — Art. 6(1)(f)",
                "3 years after the matter is closed",
              ],
              [
                "System security, logs, abuse prevention",
                "legitimate interest — Art. 6(1)(f) GDPR",
                "normally 90 days; longer only while investigating a specific incident",
              ],
              [
                "Measuring traffic and improving the service (analytics)",
                "consent — Art. 6(1)(a) GDPR and Art. 225 ZEKom-2",
                "until consent is withdrawn; cookies per the Cookie Policy",
              ],
              [
                "Advertising and measuring advertising performance",
                "consent — Art. 6(1)(a) GDPR and Art. 225 ZEKom-2",
                "until consent is withdrawn",
              ],
              [
                "Sending the newsletter",
                "consent — Art. 6(1)(a) GDPR",
                "until you unsubscribe or withdraw consent",
              ],
              [
                "Establishing or defending legal claims",
                "legitimate interest — Art. 6(1)(f) GDPR",
                "until the limitation periods expire",
              ],
            ],
          },
          {
            type: "p",
            text: "Where we rely on legitimate interest, we have carried out a balancing test between that interest and your rights. We will provide a summary on request at privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "4. Property data and data about other people",
        blocks: [
          {
            type: "p",
            text: "The address of the property you query is order data for us. Where the property relates to you (for example your home), it is also your personal data and we treat it like any other order data.",
          },
          {
            type: "p",
            text: "A report may contain public-register data relating to third parties — in particular where a land registry excerpt showing ownership or encumbrances is available for the property. We process that data solely to produce the report you ordered, on the basis of legitimate interest under Article 6(1)(f) GDPR, and only to the extent it is already publicly accessible in the register.",
          },
          {
            type: "p",
            text: "We do not build profiles of individuals from that data, do not maintain a separate database of owners, and do not use it for marketing. As the data is not obtained directly from the individual and informing each individual would involve disproportionate effort, we rely on the exemption in Article 14(5)(b) GDPR and publish this policy instead of giving individual notice.",
          },
          {
            type: "note",
            text: "As the customer, you are responsible for having a legitimate interest in querying a particular property (for example an intended purchase, lease, financing, valuation or ownership). The service must not be used to track, harass or profile individuals.",
          },
        ],
      },
      {
        title: "5. Sources that are not user personal data",
        blocks: [
          {
            type: "p",
            text: "To produce reports we use publicly accessible sources: the registers of the Surveying and Mapping Authority of the Republic of Slovenia (real estate cadastre, real estate register, Real Estate Market Register), the land registry, the register of energy performance certificates, mapping sources, and publicly published property listings. These sources are outside our control and are subject to the terms of their operators.",
          },
        ],
      },
      {
        title: "6. Cookies and similar technologies",
        blocks: [
          {
            type: "p",
            text: "We do not load non-essential cookies or similar technologies until you consent. On your first visit we show a notice where you can accept everything, refuse, or choose individual categories. Refusing is exactly as easy as accepting.",
          },
          {
            type: "p",
            text: "For sign-in and the operation of the service we use browser local storage; this is strictly necessary to provide the service you explicitly requested, so no consent is required (Article 225(2) ZEKom-2).",
          },
          { type: "p", text: "A detailed list is set out in the Cookie Policy." },
          { type: "cookieSettings", label: "Cookie settings" },
        ],
      },
      {
        title: "7. Who we share data with",
        blocks: [
          {
            type: "p",
            text: "We do not sell personal data. We share it only with processors acting on our instructions under a data processing agreement (Article 28 GDPR), and with authorities where required by law.",
          },
          {
            type: "table",
            head: ["Recipient", "Purpose", "Processing location", "Transfer safeguard"],
            rows: [
              [
                "Supabase, Inc.",
                "database, user sign-in, file storage, server functions",
                "EU (Frankfurt, eu-central-1); company based in the USA",
                "data processing agreement and standard contractual clauses",
              ],
              [
                "Railway Corp.",
                "hosting the backend server, job queues and PDF generation",
                "USA / EU",
                "standard contractual clauses",
              ],
              [
                "Vercel Inc.",
                "hosting and delivering the website",
                "EU / USA",
                "standard contractual clauses, EU-US Data Privacy Framework",
              ],
              [
                "Cloudflare, Inc.",
                "network proxy, protection against abuse and attacks",
                "global network",
                "standard contractual clauses, EU-US Data Privacy Framework",
              ],
              [
                "Stripe Payments Europe, Ltd.",
                "processing payments, fraud prevention (independent controller for that purpose)",
                "Ireland, USA",
                "standard contractual clauses",
              ],
              [
                "Anthropic, PBC",
                "generating the written parts of the report with a language model",
                "USA",
                "standard contractual clauses; no use of data for model training",
              ],
              [
                "Google Ireland Ltd. / Google LLC",
                "maps, address search and geocoding; the Gemini language model; Google Analytics (only upon consent)",
                "Ireland, USA",
                "standard contractual clauses, EU-US Data Privacy Framework",
              ],
              [
                "Meta Platforms Ireland Ltd.",
                "measuring advertising performance (only upon marketing consent)",
                "Ireland, USA",
                "standard contractual clauses, EU-US Data Privacy Framework",
              ],
              [
                "Resend, Inc.",
                "sending transactional email",
                "USA",
                "standard contractual clauses",
              ],
              [
                "MailerLite Limited",
                "sending the newsletter (only upon consent)",
                "European Union",
                "no third-country transfer",
              ],
            ],
          },
          {
            type: "p",
            text: "We may also disclose data to a court, tax or supervisory authority, the police or another competent body where we are obliged to, and to our accountant, lawyer or auditor to the extent needed for their work. In the event of a corporate reorganisation or sale of the business, data may transfer to the successor; we will inform you in advance.",
          },
        ],
      },
      {
        title: "8. Transfers to third countries",
        blocks: [
          {
            type: "p",
            text: "Some processors are established or have support operations in the United States. In those cases the transfer relies on the European Commission's adequacy decision for the EU-US Data Privacy Framework (where the recipient is certified) or on the Commission's standard contractual clauses, supplemented by a transfer impact assessment and additional technical measures such as encryption in transit and at rest and access restrictions.",
          },
          {
            type: "p",
            text: "You may request a copy of the safeguards used at privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "9. Automated decision-making and profiling",
        blocks: [
          {
            type: "p",
            text: "A report, including the indicative property value estimate, is produced automatically without human review. This processing concerns the property and does not produce legal effects concerning an individual or similarly significantly affect them, so it is not automated decision-making within the meaning of Article 22(1) GDPR.",
          },
          {
            type: "p",
            text: "We do not use your personal data for automated creditworthiness or risk scoring or similar ranking of individuals.",
          },
        ],
      },
      {
        title: "10. Security of processing",
        blocks: [
          {
            type: "ul",
            items: [
              "encryption in transit (TLS) and encryption of data at rest,",
              "least-privilege access control and separate administrator accounts,",
              "row-level security in the database, so a user can reach only their own records,",
              "logging of administrative actions,",
              "regular backups and verification that they can be restored,",
              "data processing agreements and vetting of processors.",
            ],
          },
          {
            type: "p",
            text: "In the event of a personal data breach likely to result in a high risk to your rights and freedoms, we will inform you without undue delay and notify the Information Commissioner within 72 hours.",
          },
        ],
      },
      {
        title: "11. Your rights",
        blocks: [
          { type: "p", text: "In relation to your personal data you have the right to:" },
          {
            type: "ul",
            items: [
              "access your data and obtain a copy of it (Article 15 GDPR),",
              "rectification of inaccurate and completion of incomplete data (Article 16),",
              "erasure of data, the “right to be forgotten” (Article 17),",
              "restriction of processing (Article 18),",
              "data portability in a machine-readable format (Article 20),",
              "object to processing based on legitimate interest, and to object at any time to processing for direct marketing (Article 21),",
              "withdraw consent at any time, without affecting the lawfulness of processing before withdrawal (Article 7).",
            ],
          },
          {
            type: "p",
            text: "Send requests to privacy@nreport.ai. We respond within one month of receipt at the latest; for complex requests we may extend this by two months and will tell you if we do. Exercising your rights is free of charge; for manifestly unfounded or excessive requests we may charge a reasonable fee or refuse the request. For security reasons we may ask for additional information to verify your identity.",
          },
          {
            type: "p",
            text: "You can also request deletion of your account and data through the dedicated form on the website. We must retain some data despite a deletion request where the law requires it — in particular issued invoices for the tax retention period.",
          },
          {
            type: "p",
            text: "If you believe the processing of your personal data infringes the law, you have the right to lodge a complaint with the supervisory authority:",
          },
          {
            type: "links",
            items: [
              {
                label:
                  "Information Commissioner of the Republic of Slovenia, Dunajska cesta 22, 1000 Ljubljana — www.ip-rs.si",
                href: "https://www.ip-rs.si/",
              },
            ],
          },
          {
            type: "p",
            text: "You may also lodge a complaint with the supervisory authority in the country of your habitual residence or of the alleged infringement.",
          },
        ],
      },
      {
        title: "12. Is providing data mandatory",
        blocks: [
          {
            type: "p",
            text: "Providing your email address and the property details is a condition for concluding and performing the contract — without them we cannot produce and deliver a report. Providing data for the newsletter and consenting to non-essential cookies are entirely voluntary; refusing them does not affect your use of the service.",
          },
        ],
      },
      {
        title: "13. Children",
        blocks: [
          {
            type: "p",
            text: "The service is not intended for persons under 18 and we do not knowingly collect their data. If we find that we have processed a minor's data without an appropriate basis, we delete it. If you believe this has happened, tell us at privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "14. Links to other websites",
        blocks: [
          {
            type: "p",
            text: "The website contains links to third-party sites (for example official registers or listing portals). We are not responsible for their content or their handling of personal data; we recommend reading their privacy policies.",
          },
        ],
      },
      {
        title: "15. Changes to this policy",
        blocks: [
          {
            type: "p",
            text: "We may update this policy when the service, our processors or the law change. Every version carries the date of the last update. We notify registered users of material changes by email or by a notice on the website; where a change requires your consent, we will obtain it separately.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  cookies: {
    eyebrow: legalEyebrow,
    title: "Cookie Policy",
    updated,
    intro:
      "This policy explains which cookies and similar technologies the nreport.ai website, operated by Noventis d.o.o., uses, why it uses them, and how you can change your choice at any time.",
    sections: [
      {
        title: "1. What cookies are",
        blocks: [
          {
            type: "p",
            text: "Cookies are small text files a website stores in your browser. Local storage, session storage and tracking pixels play a similar role. In this policy we refer to all of them collectively as “cookies”.",
          },
          {
            type: "p",
            text: "Cookies let a website remember your sign-in and settings; some are also used to measure traffic and for advertising.",
          },
        ],
      },
      {
        title: "2. Legal basis and your consent",
        blocks: [
          {
            type: "p",
            text: "Under Article 225(1) of the Electronic Communications Act (ZEKom-2) and Article 6(1)(a) GDPR, we do not store or read non-essential cookies until you consent. Consent is voluntary, specific and unambiguous, and refusing is exactly as easy as accepting.",
          },
          {
            type: "p",
            text: "Strictly necessary cookies do not require consent because they are essential to provide the service you explicitly requested (Article 225(2) ZEKom-2).",
          },
          {
            type: "p",
            text: "You can withdraw or change your consent at any time. Withdrawal does not affect the lawfulness of processing before withdrawal.",
          },
          { type: "cookieSettings", label: "Cookie settings" },
        ],
      },
      {
        title: "3. Categories of cookies we use",
        subsections: [
          {
            title: "Strictly necessary (always active)",
            blocks: [
              {
                type: "p",
                text: "These enable sign-in, session persistence, form security and payment. The website does not work without them. They do not require your consent and cannot be switched off.",
              },
              {
                type: "table",
                head: ["Name / technology", "Provider", "Purpose", "Duration"],
                rows: [
                  [
                    "sb-…-auth-token (local storage)",
                    "nReport / Supabase",
                    "keeping the user signed in",
                    "until sign-out or browser data is cleared",
                  ],
                  [
                    "nreport-cookie-consent (local storage)",
                    "nReport",
                    "storing your cookie choice",
                    "until withdrawn or browser data is cleared",
                  ],
                  [
                    "selectedAddress, selectedPropertyType and similar (local storage)",
                    "nReport",
                    "retaining entered details between order steps",
                    "until the order is completed",
                  ],
                  [
                    "metaPurchaseContext (session storage)",
                    "nReport",
                    "carrying purchase details to the confirmation page",
                    "until the tab is closed",
                  ],
                  [
                    "__stripe_mid, __stripe_sid",
                    "Stripe",
                    "processing payment and preventing fraud; set only on the payment page",
                    "1 year / 30 minutes",
                  ],
                ],
              },
            ],
          },
          {
            title: "Analytics (only with your consent)",
            blocks: [
              {
                type: "p",
                text: "These help us understand how many visitors the site has, which pages they visit and where they run into problems. We use the data to improve the service.",
              },
              {
                type: "table",
                head: ["Name", "Provider", "Purpose", "Duration"],
                rows: [
                  [
                    "_ga, _ga_*",
                    "Google Analytics 4 (Google Ireland Ltd.)",
                    "distinguishing visitors and measuring traffic",
                    "up to 2 years",
                  ],
                  [
                    "_gid, _gat",
                    "Google Analytics 4",
                    "distinguishing visitors, throttling request rate",
                    "24 hours / 1 minute",
                  ],
                ],
              },
              {
                type: "p",
                text: "We run Google Analytics with Consent Mode; without your consent the script is not loaded at all, and Google Signals and ad personalisation are disabled.",
              },
            ],
          },
          {
            title: "Marketing (only with your consent)",
            blocks: [
              {
                type: "p",
                text: "These let us measure the performance of our advertising campaigns and show more relevant ads on Meta platforms.",
              },
              {
                type: "table",
                head: ["Name", "Provider", "Purpose", "Duration"],
                rows: [
                  [
                    "_fbp",
                    "Meta Platforms Ireland Ltd.",
                    "identifying the browser for conversion measurement",
                    "up to 3 months",
                  ],
                  [
                    "_fbc",
                    "Meta Platforms Ireland Ltd.",
                    "recording the ad click that brought you to the site",
                    "up to 3 months",
                  ],
                ],
              },
              {
                type: "p",
                text: "If you do not allow marketing cookies, we do not load the Meta pixel and we also do not report your purchase to Meta from our server.",
              },
            ],
          },
        ],
      },
      {
        title: "4. How to change or withdraw consent",
        blocks: [
          {
            type: "p",
            text: "You can change your choice at any time using the button below or the “Cookie settings” link in the website footer.",
          },
          { type: "cookieSettings", label: "Cookie settings" },
          {
            type: "p",
            text: "When consent is withdrawn we stop loading the relevant scripts and delete the cookies we are able to remove. You can also remove cookies already stored in your browser through your browser settings.",
          },
        ],
      },
      {
        title: "5. Managing cookies in your browser",
        blocks: [
          {
            type: "ul",
            items: [
              "Chrome: Settings › Privacy and security › Third-party cookies",
              "Firefox: Settings › Privacy & Security › Cookies and Site Data",
              "Safari: Settings › Privacy › Manage Website Data",
              "Edge: Settings › Cookies and site permissions",
            ],
          },
          {
            type: "p",
            text: "Blocking all cookies may prevent sign-in and purchase, because the website cannot maintain your session.",
          },
        ],
      },
      {
        title: "6. Third-party policies",
        blocks: [
          {
            type: "links",
            items: [
              { label: "Google — Privacy Policy", href: "https://policies.google.com/privacy" },
              { label: "Meta — Privacy Policy", href: "https://www.facebook.com/privacy/policy" },
              { label: "Stripe — Privacy Policy", href: "https://stripe.com/privacy" },
              { label: "Supabase — Privacy Policy", href: "https://supabase.com/privacy" },
            ],
          },
        ],
      },
      {
        title: "7. Changes and contact",
        blocks: [
          {
            type: "p",
            text: "We update this cookie policy when the technologies we use change. Where the set of cookies changes materially, we ask for your consent again.",
          },
          {
            type: "p",
            text: "For questions about cookies write to privacy@nreport.ai. More about the processing of personal data is set out in the Privacy Policy.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  dataDeletion: {
    title: "Personal data deletion",
    subtitle:
      "Request erasure of your personal data under Article 17 of the General Data Protection Regulation (GDPR).",
    alert:
      "Important: deletion is irreversible. Once deleted, you will no longer be able to access your account, purchased credits or generated reports.",
    formTitle: "Data deletion request",
    formDesc: "Complete the form below to request erasure of your personal data.",
    emailLabel: "Email address *",
    emailPlaceholder: "your.email@example.com",
    emailHint: "Enter the email address associated with your account.",
    reasonLabel: "Reason for deletion (optional)",
    reasonPlaceholder: "Describe the reason for deleting your data...",
    deletedDataTitle: "Data that will be deleted:",
    deletedDataItems: [
      "Account data (email, name, settings)",
      "Order history and credit usage",
      "Stored reports and analyses",
      "Uploaded files and attachments",
      "Consent records and preferences",
    ],
    submit: "Send deletion request",
    contact: "Questions? Contact us at",
    successTitle: "Request sent successfully",
    successDesc:
      "We have received your request. We will handle it within one month of receipt at the latest, as required by Article 12 GDPR.",
    processingTitle: "Processing time",
    processingDesc:
      "We handle requests within one month at the latest. Tax law requires us to keep issued invoices for 10 years, so those remain stored even after the account is deleted.",
    securityTitle: "Data security",
    securityDesc:
      "We may ask you to confirm your identity before deletion. Data is securely erased from our systems, and from backups within 30 days at the latest.",
    backHome: "Back to home",
    toastEmailRequired: "Please enter your email address.",
    toastSendFailed: "We could not send your request. Please try again or write to info@nreport.ai.",
    toastSuccessTitle: "Request sent",
    toastSuccessDesc: "Your data deletion request has been sent successfully.",
  },
};
